import React, { useState, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";
import MapPicker from "./screens/new/MapPicker";
import LocationInput from "./screens/new/LocationInput";
import Modal from "react-modal";
import { FaMapMarkerAlt, FaHistory, FaTimes } from "react-icons/fa";
import "./App.css";

const LOCAL_STORAGE_KEY_DESTINATION = "recentDestinationLocations";
const LOCAL_STORAGE_KEY_PLACE_CACHE = "placeNameCache";

const LocateDestinationFilterPicker = () => {
  const [destinationLocation, setDestinationLocation] = useState("");
  const [destinationCoordinates, setDestinationCoordinates] = useState(null);
  const [isDestinationMapOpen, setDestinationMapOpen] = useState(false);
  const [recentDestinationLocations, setRecentDestinationLocations] = useState([]);
  const [placeNameCache, setPlaceNameCache] = useState({});

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries: ["places"],
  });

  const reverseGeocode = async (lat, lng, apiKey) => {
    try {
      // Create a cache key from coordinates (rounded to reduce duplicates)
      const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      
      // Check if we already have this place name cached
      if (placeNameCache[cacheKey]) {
        console.log('Using cached place name for:', cacheKey, placeNameCache[cacheKey]);
        return placeNameCache[cacheKey];
      }
      
      console.log('Fetching place name from API for:', cacheKey);
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
      const response = await fetch(geocodeUrl);
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          const placeName = data.results[0].formatted_address;
          
          // Cache the place name for future use
          const updatedCache = { ...placeNameCache, [cacheKey]: placeName };
          setPlaceNameCache(updatedCache);
          localStorage.setItem(LOCAL_STORAGE_KEY_PLACE_CACHE, JSON.stringify(updatedCache));
          
          console.log('Cached place name:', cacheKey, '->', placeName);
          return placeName;
        }
      }
      
      // Fallback to coordinates if API fails
      const fallbackName = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      const updatedCache = { ...placeNameCache, [cacheKey]: fallbackName };
      setPlaceNameCache(updatedCache);
      localStorage.setItem(LOCAL_STORAGE_KEY_PLACE_CACHE, JSON.stringify(updatedCache));
      
      return fallbackName;
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  useEffect(() => {
    const destinationLocations =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_DESTINATION)) || [];
    setRecentDestinationLocations(destinationLocations);
    
    const cachedPlaces = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_PLACE_CACHE)) || {};
    setPlaceNameCache(cachedPlaces);
  }, []);

  const updateRecentLocations = (address, coords) => {
    const recent = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_DESTINATION)) || [];
    const updated = recent.filter((loc) => loc.address !== address);
    const newEntry = { address, coords };
    const newRecentLocations = [newEntry, ...updated].slice(0, 5);

    localStorage.setItem(
      LOCAL_STORAGE_KEY_DESTINATION,
      JSON.stringify(newRecentLocations)
    );

    setRecentDestinationLocations(newRecentLocations);
  };

  const handleLocationSelect = async (coords, address) => {
    let finalAddress = address;

    if (!finalAddress || finalAddress.trim() === "") {
      finalAddress = await reverseGeocode(
        coords.lat,
        coords.lng,
        getGoogleMapsApiKey()
      );
    }

    setDestinationCoordinates(coords);
    setDestinationLocation(finalAddress);
    setDestinationMapOpen(false);
    updateRecentLocations(finalAddress, coords);
  };

  const handleRecentLocationSelect = (coords, address) => {
    setDestinationCoordinates(coords);
    setDestinationLocation(address);
  };

  const handleSubmit = async () => {
    if (!destinationCoordinates) {
      alert("Please select a destination.");
      return;
    }

    let finalDestinationLocation = destinationLocation;

    if (!finalDestinationLocation || finalDestinationLocation.trim() === "") {
      finalDestinationLocation = await reverseGeocode(
        destinationCoordinates.lat,
        destinationCoordinates.lng,
        getGoogleMapsApiKey()
      );
      setDestinationLocation(finalDestinationLocation);
    }

    const message = {
      destination: {
        ...destinationCoordinates,
        address: finalDestinationLocation,
      },
    };

    console.log('Sending message to mobile app:', message);
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  };

  const customModalStyles = {
    content: {
      top: "0%",
      left: "0%",
      right: "0%",
      bottom: "0%",
      width: "100%",
      height: "100%",
      padding: "0",
      border: "none",
      borderRadius: "0",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.75)",
    },
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">
          Destination Picker
        </h1>

        <div className="mb-8 p-4 border rounded-lg shadow-sm bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Destination Location
          </label>

          <div className="flex gap-2">
            <div className="flex-1">
              <LocationInput
                placeholder="Enter destination location"
                value={destinationLocation}
                onChange={setDestinationLocation}
                onSelect={(coords, address) =>
                  handleLocationSelect(coords, address)
                }
              />
            </div>

            <button
              onClick={() => setDestinationMapOpen(true)}
              className="p-2 bg-[#ffc000] hover:bg-[#e3ab00] text-white rounded-lg transition-colors"
            >
              <FaMapMarkerAlt className="w-5 h-5" />
            </button>
          </div>

          {recentDestinationLocations.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <FaHistory className="w-4 h-4" />
                <span>Recent Locations</span>
              </div>

              <div className="space-y-2">
                {recentDestinationLocations.map((location, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleRecentLocationSelect(
                        location.coords,
                        location.address
                      )
                    }
                    className="p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors text-sm"
                  >
                    {location.address}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!destinationCoordinates}
          className="w-full bg-[#ffc000] text-black hover:bg-[#e3ab00] py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          Continue
        </button>
      </div>

      <Modal
        isOpen={isDestinationMapOpen}
        onRequestClose={() => setDestinationMapOpen(false)}
        style={customModalStyles}
      >
        <div className="relative pt-9">
          <div className="p-4 border-b">
            <h2 className="text-xl font-semibold">
              Select Destination Location
            </h2>
          </div>

          <button
            onClick={() => setDestinationMapOpen(false)}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
          >
            <FaTimes className="w-5 h-5" />
          </button>

          <MapPicker
            onLocationSelect={(coords, address) =>
              handleLocationSelect(coords, address)
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default LocateDestinationFilterPicker;